import React, {useEffect, useRef, useState} from "react";
import { ExclamationTriangleIcon, CalendarIcon } from "@heroicons/react/24/outline";
import Label from "./Label";
import { useIntl } from "react-intl";
import CheckTranslations from "./CheckTranslations";
import { validateEmail } from "@utils/validate-email";
import Icon from "./Icon";

const validator = (type, value) => {
  switch (type) {
    case "email":
      return validateEmail(value);

    default:
      return true;
  }
};

const Input = ({
  translations,
  value,
  label,
  name,
  type = "text",
  validation,
  required,
  errorMessage,
  errorType = "error",
  showCalendar,
  checkTranslation,
  showErrorDialog = true,
  validateOnMount = false,
  numberOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
  hideBottomMargin = false,
  withMargin = true,
  disabled = false,
  leftIcon= null,
  materialTable = false,
  inputTextColor = "text-black-500",
  validateOnBlur = true,
  iconClasses = "",
  title="",
  wFull = true,
  ...rest
}) => {
  const [valid, setValid] = useState(true);
  const [focus, setFocus] = useState(false);
  const intl = useIntl();
  const inputFieldRef = useRef(null);
  let inputValue = value === 0 ? value : value ? value : "";

  if (inputValue && type === "number" && !focus) {
    inputValue = intl.formatNumber(inputValue, numberOptions);
  }

  useEffect(() => {
    // trigger validation in case this component has an init value
    if (validateOnMount || (validation && inputValue !== "")) {
      inputFieldRef.current.focus();
      inputFieldRef.current.blur();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`${withMargin ? "mb-1" : null} ${wFull ? "w-full" : ""}`}>
      {label ? <Label disabled={disabled}>{label}</Label> : null}
      <div className="mt-1 relative">
        {typeof showCalendar !== "undefined" ? (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <CalendarIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        ) : null}
        <input
          onFocus={() => {
            if (type === "number") {
              setFocus(true);
            }
            setValid(true);
          }}
          onBlur={(e) => {
            if (type === "number") {
              setFocus(false);
            }
            if (required && validateOnBlur) {
              if (!e.target.value) {
                setValid(false);
                return;
              }

              if (validation) {
                setValid(validation(e.target.value));
                return;
              }

              setValid(validator(type, e.target.value));
            }
          }}
          onWheel={e => e.target.blur()}
          type={type === "number" ? (focus ? type : "text") : type}
          name={type}
          className={`${leftIcon && "pl-10"} px-2 py-2 ${inputTextColor} disabled:text-black-300 border${materialTable && "-2"} ${!materialTable ? "focus:ring-am-500 focus:border-black-500": "focus:shadow-none focus:border-kerakoll-400"} block w-full sm:text-sm ${materialTable ?"outline border-kerakoll-400 mt-1 mb-0 bg-gray-100" : "border-gray-300"} ${disabled ? "opacity-50 cursor-not-allowed bg-gray-200" : ""} ${
            typeof showCalendar !== "undefined" ? "pl-10" : "pr-3"
          }`}
          value={inputValue}
          ref={inputFieldRef}
          disabled={disabled}
          autoComplete={type === "number" ? "off" : "on"}
          title={title}
          {...(required ? {required: "required"} : {})}
          {...rest}
        />
        {leftIcon && (<Icon iconName={leftIcon} className={`absolute inset-y-2 left-1  ml-1 mr-1 h-5 w-5 ${iconClasses}`} />)}
        {!valid ? (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationTriangleIcon
              className={`h-5 w-5 ${errorType === "error" ? "text-red-500" : "text-yellow-500"}`}
              aria-hidden="true"
            />
          </div>
        ) : null}
      </div>
      {checkTranslation && value && !focus && translations ? (
        <CheckTranslations
          value={value}
          checkTranslation={checkTranslation}
          translations={translations}
          disabled={disabled}
        />
      ) : null}
      {!showErrorDialog || showCalendar ? null : !valid && errorMessage ? (
        <p className={`mt-1 text-xs ${errorType === "error" ? "text-red-600" : "text-yellow-600"}`} id="email-error">
          {errorMessage}
        </p>
      ) : (
        !hideBottomMargin && <div className="mt-2 h-2" />
      )}
    </div>
  );
};

export default Input;
